import React, { useCallback, useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { graphql } from 'react-relay';

import AutomationIcon from '@atlaskit/icon/core/automation';
import { usePublish } from '@atlaskit/rovo-triggers/main';
import {
	type ConversationAction,
	HandleActionType,
} from '@atlassian/rovo-conversation-actions/types';

import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { fg } from '@confluence/feature-gating';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { keys } from '@confluence/storage-manager/entry-points/LocalStorageKeys.ts';
import { confluenceLocalStorageInstance } from '@confluence/storage-manager/entry-points/storageInstance.ts';
import type { StatusUpdateChild } from '@confluence/status-update-signal/entry-points/getStatusUpdateSignalMatch.ts';

import { useStudioPermissions } from '../hooks/useStudioPermissions.ts';
import { markDiscoveryExperienceAsSeen } from '../utils/localStorage.ts';
import type {
	useStatusUpdatesAutomationNudgeActionQuery$data,
	useStatusUpdatesAutomationNudgeActionQuery$variables,
} from './__generated__/useStatusUpdatesAutomationNudgeActionQuery.graphql.ts';
import { useMatchesStatusUpdate } from './useMatchesStatusUpdateSignal.ts';
import { ConfluenceEdition } from '@confluence/change-edition/entry-points/ConfluenceEdition.ts';

const STATUS_UPDATES_NUDGE_ID = 'automation-discovery-status-updates-nudge';
const ROVO_CHAT_SOURCE = 'automation-discovery-status-updates-nudge';

const i18n = defineMessages({
	title: {
		id: 'automation-discovery.status-updates-automation-discovery-nudge-action.title',
		defaultMessage: 'Automate this status update',
		description:
			'Heading in the status updates automation discovery nudge inviting the user to automate status update reporting.',
	},
	iconLabel: {
		id: 'automation-discovery.status-updates-automation-discovery-nudge-action.icon-label',
		defaultMessage: 'Automation icon',
		description:
			'Accessible label for the icon in the status updates automation discovery nudge action.',
	},
});

const BUILD_AUTOMATION_PROMPT = `/build-automation Help me create an automation for newly published Confluence status updates.

Create an automation that:

1. Triggers when a Confluence page is published.
2. Continues only when the page title contains “status update”, case-insensitively.
3. Runs the out-of-the-box "Jira Work Agent" agent.
4. Sends the agent output in an email to the current user.
5. Does not use a recipient other than the current user.

Configure the Status update reporter using this prompt exactly:

\`\`\`
You are writing the body of an email sent to stakeholders after a status update is published in Confluence.
Status update page title: {{page.title}}
Status update page URL: {{page.url}}
Status update page content:
{{page.body.adf}}
Base the email on the page content above. It is the authoritative source. You may also look up closely related status updates, but only to clarify a decision or an outstanding action that the page content leaves ambiguous. Never state anything that is not supported by the page content or by a source you actually retrieved.
Respond with HTML only. Do not use markdown, code fences, or commentary before or after the HTML. Use only the tags h2, h3, p, ul, ol, li, a, strong and em. Do not emit html, head, body or style tags and do not use inline CSS.
Structure the email in this order:
A section headed Summary. Give a short paragraph describing overall progress and current status.
A section headed Highlights. Give a bulleted list of the most significant progress, wins and changes since the last update, one line each.
A section headed Risks and blockers. Give a bulleted list of anything at risk, blocked or needing a decision. If there are none, use one paragraph saying so.
A section headed Next steps. Give a bulleted list where every item begins with the responsible person’s name in a strong element, followed by a colon and the task, including any stated due date. Use Unassigned when no owner is identified. If there are none, use one paragraph saying so.
Close with a paragraph containing an anchor element whose href is the status update page URL and whose text is the page title.
Include links to referenced pages, work items, or documents as anchor elements with descriptive labels and absolute URLs. Do not invent details, owners, or dates. Keep the email under 500 words and write in plain, direct language.
\`\`\`
`;

interface UseStatusUpdatesAutomationNudgeActionProps {
	contentId: string;
}

export const useStatusUpdatesAutomationNudgeAction = ({
	contentId,
}: UseStatusUpdatesAutomationNudgeActionProps): ConversationAction | null => {
	const { formatMessage } = useIntl();
	const publishRovoChat = usePublish('ai-mate');
	const { isRovoLLMEnabled, edition } = useSessionData();
	const hasUserSeenAutomationDiscoveryMessage = confluenceLocalStorageInstance.getItemAsBoolean(
		keys.AUTOMATION_DISCOVER_STATUS_UPDATES,
	);
	const hasBaseEligibility =
		(edition === ConfluenceEdition.STANDARD || edition === ConfluenceEdition.PREMIUM) &&
		!hasUserSeenAutomationDiscoveryMessage &&
		isRovoLLMEnabled &&
		Boolean(contentId) &&
		fg('cc_automations_discovery_nudges');

	const { data } = useQueryInterop<
		useStatusUpdatesAutomationNudgeActionQuery$data,
		useStatusUpdatesAutomationNudgeActionQuery$variables
	>(
		graphql`
			query useStatusUpdatesAutomationNudgeActionQuery($contentId: ID!) {
				singleContent(id: $contentId) {
					id
					title
					ancestors {
						id
						title
					}
					metadata {
						sourceTemplateEntityId
					}
					children(first: 25) {
						nodes {
							id
							title
							metadata {
								createdDate
							}
						}
					}
				}
			}
		`,
		{
			variables: { contentId },
			skip: !hasBaseEligibility,
		},
	);

	const content = data?.singleContent;
	const ancestors = content?.ancestors;
	const parentTitle = ancestors?.[ancestors.length - 1]?.title;
	const children = useMemo<StatusUpdateChild[]>(
		() =>
			content?.children?.nodes?.flatMap((child) =>
				child?.title && child.metadata?.createdDate
					? [{ title: child.title, createdAt: child.metadata.createdDate }]
					: [],
			) ?? [],
		[content?.children?.nodes],
	);
	const { matches: matchesStatusUpdate } = useMatchesStatusUpdate({
		title: content?.title,
		parentTitle,
		sourceTemplateEntityId: content?.metadata?.sourceTemplateEntityId,
		children,
	});

	const { canCreateStudioAutomation } = useStudioPermissions({
		skip: !matchesStatusUpdate || !hasBaseEligibility,
	});

	const handleLocalStorageUpdate = useCallback(() => {
		markDiscoveryExperienceAsSeen(keys.AUTOMATION_DISCOVER_STATUS_UPDATES);
	}, []);

	return useMemo<ConversationAction | null>(
		() =>
			canCreateStudioAutomation && matchesStatusUpdate && hasBaseEligibility
				? {
						id: STATUS_UPDATES_NUDGE_ID,
						title: formatMessage(i18n.title),
						icon: () => <AutomationIcon size="small" label={formatMessage(i18n.iconLabel)} />,
						handleType: HandleActionType.Skill,
						type: 'skill-action',
						staticPrompt: BUILD_AUTOMATION_PROMPT,
						onClick: () => {
							publishRovoChat({
								type: 'chat-new',
								source: ROVO_CHAT_SOURCE,
								openChat: true,
								openChatMode: 'sidebar',
								data: {
									dialogues: [],
									prompt: BUILD_AUTOMATION_PROMPT,
									placeholderType: 'skill',
								},
							});
						},
						nudgeContext: {
							id: STATUS_UPDATES_NUDGE_ID,
							selfEligible: true,
							onAccept: handleLocalStorageUpdate,
							onDismiss: (reason) => {
								if (reason?.dismissedMethod === 'manual') {
									handleLocalStorageUpdate();
								}
							},
						},
					}
				: null,
		[
			canCreateStudioAutomation,
			formatMessage,
			handleLocalStorageUpdate,
			hasBaseEligibility,
			matchesStatusUpdate,
			publishRovoChat,
		],
	);
};
